<template>
  <div>
    <!-- Hero Section -->
    <div class="main-banner">
      <div class="main-banner-content mt-14 !justify-center">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('aboutPage.hero.title') }}
          </div>
          <div class="banner-description">
            {{ $t('aboutPage.hero.subtitle') }}
          </div>
          <div class="banner-description mt-4">
            {{ $t('aboutPage.hero.description') }}
          </div>
        </div>
        <div class="banner-image-container">
          <NuxtImg src="https://cdn-web.smsbat.com/uploads/2024/viber_bots.png" width="800" height="600" format="webp" loading="eager" fetchpriority="high" class="w-full h-auto object-contain" />
        </div>
      </div>
    </div>

    <!-- Mission Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('aboutPage.mission.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content mb-8">
            <p>{{ $t('aboutPage.mission.description') }}</p>
          </div>

          <div class="about-section">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div v-for="(value, index) in tm('aboutPage.mission.values')" :key="index" class="about-item">
                <div class="about-item-text">
                  <h3 class="highlight-blue font-bold text-xl mb-2">{{ rt(value.title) }}</h3>
                  <p>{{ rt(value.description) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Services Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('aboutPage.services.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content mb-8">
            <p>{{ $t('aboutPage.services.description') }}</p>
          </div>

          <div class="about-section">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div v-for="(service, index) in tm('aboutPage.services.list')" :key="index" class="service-card">
                <div class="service-card-content">
                  <h3 class="highlight-blue font-bold text-lg mb-3">{{ rt(service.title) }}</h3>
                  <p>{{ rt(service.description) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Why Choose SMSBAT Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('aboutPage.whyChoose.title') }}</div>
        </div>
        <div class="section-content">
          <div class="about-section">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div v-for="(point, index) in tm('aboutPage.whyChoose.points')" :key="index" class="about-item">
                <div class="about-item-text">
                  <h3 class="highlight-blue font-bold text-xl mb-2">{{ rt(point.title) }}</h3>
                  <p>{{ rt(point.description) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('aboutPage.stats.title') }}</div>
          <p class="text-gray-600 mt-4">{{ $t('aboutPage.stats.description') }}</p>
        </div>
        <div class="section-content">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div v-for="(metric, index) in tm('aboutPage.stats.metrics')" :key="index" class="stat-item text-center">
              <div class="stat-number highlight-blue font-bold text-3xl md:text-4xl mb-2">{{ rt(metric.number) }}</div>
              <div class="stat-label text-gray-600">{{ rt(metric.label) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-content text-center">
          <div class="cta-content">
            <h2 class="section-head-title mb-4">{{ $t('aboutPage.cta.title') }}</h2>
            <p class="text-gray-600 mb-8">{{ $t('aboutPage.cta.description') }}</p>
            <div class="banner-action flex flex-wrap items-center gap-4 justify-center">
              <NuxtLink :to="localePath('/register-bot')" class="nav-link button filled">
                <p>{{ $t('aboutPage.cta.button') }}</p>
              </NuxtLink>
              <NuxtLink :to="localePath('/contacts')" class="button outlined">
                <p>{{ $t('mainNav.contacts') }}</p>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const localePath = useLocalePath();
const { t, tm, rt } = useI18n() as any

useHead({
  titleTemplate: () => t("seo.layouts./about.title") || t("seo.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./about.description") || t("seo.description") },
    { property: "og:title", content: () => t("seo.layouts./about.title") || t("seo.title") },
    { property: "og:description", content: () => t("seo.layouts./about.description") || t("seo.description") },
    { property: "og:image", content: "https://cdn-web.smsbat.com/uploads/2024/viber_bots.png" },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
})
</script>

<style lang="scss" scoped>
.service-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
}

.service-card-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.stat-item {
  padding: 20px;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  font-size: 0.9rem;
  font-weight: 500;
}

.cta-content {
  max-width: 600px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .stat-number {
    font-size: 2rem;
  }

  .service-card {
    padding: 20px;
  }
}
</style>